import {
  GetAvailableSelectivityCopyActionsRequest,
  GetAvailableSelectivityProfilesForMergeIntoMessageRequest,
  GetSelectivityFieldsByDocTypeRequest,
  GetSelectivitySourcesRequest,
  GetSelectivityTemplatesRequest,
  IGetAvailableSelectivityCopyActionsRequest,
  IGetAvailableSelectivityCopyActionsResponse,
  IGetAvailableSelectivityProfilesForMergeIntoMessageRequest,
  IGetAvailableSelectivityProfilesForMergeIntoMessageResponse,
  IGetSelectivityFieldsByDocTypeRequest,
  IGetSelectivityFieldsByDocTypeResponse,
  IGetSelectivitySourcesRequest,
  IGetSelectivitySourcesResponse,
  IGetSelectivityTemplateDetailsResponse,
  IGetSelectivityTemplatesRequest,
  IGetSelectivityTemplatesResponse,
  IKeyValuePairOfIntegerAndOlmField,
  ISelectivityTemplateGridDto,
  KeyValuePairOfIntegerAndOlmField,
  OlmField,
  SelectivitySource,
} from '@/api/client';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IErrorDetails, IRequestState } from './interfaces';
import Api from '@/api/helpers';
import { State } from '@progress/kendo-data-query';
import { GridCellProps } from '@progress/kendo-react-grid';

export interface ISelectivitySource {
  name: string;
  source: SelectivitySource | undefined;
}

export interface ISelectivityAction {
  name: string;
  action: number;
}

export interface ISelectivityProfile {
  name: string;
  profileUID: number;
  primaryKey: number;
}
export interface ISelectivityField extends IKeyValuePairOfIntegerAndOlmField {
  name: string;
}

interface ITemplateDtoWithExpand extends ISelectivityTemplateGridDto {
  expanded?: boolean;
}

export interface ITemplateDetailsGrid extends IGetSelectivityTemplateDetailsResponse {
  timeSet: number;
}

export interface ISelectivityState {
  sources: IRequestState & { list: ISelectivitySource[] };
  copyActions: IRequestState & { list: ISelectivityAction[] };
  selectedCopyAction: ISelectivityAction | null;
  selectedSource: ISelectivitySource | null;
  profiles: IRequestState & { list: ISelectivityProfile[] };
  selectedProfile: ISelectivityProfile | null;
  showInclusionToggle: boolean;
  showFirstVantageButton: boolean;
  fieldsByDocType: IRequestState & { list: ISelectivityField[]; nonStandaloneList: ISelectivityField[] };
  canAddCriteria: boolean;
  hasVisitedSelectivity: boolean;
  hasInclusionOperationChanged: boolean;
  hasVantageQueryChanges: boolean;
  hasAnySelectivityDropdownChanged: boolean;
  previousSelectedSource: ISelectivitySource | null;
  previousSelectedSourcesLength: number;
  inclusionDropdownOpened: boolean;
  wheneverUnlessToggleChanged: boolean;
  selectivityTemplates: IRequestState & { templateList: ITemplateDtoWithExpand[]; gridDataState: State };
  expandedTemplateDetails: { [id: number]: ITemplateDetailsGrid } | null;
  maximumLogicalBlockId: number;
}

const listStateObject = { list: [], isRequestActive: false, errorDetails: { statusCode: null, userMessage: null } };

const initialState: ISelectivityState = {
  sources: { ...listStateObject },
  copyActions: { ...listStateObject },
  selectedCopyAction: null,
  selectedSource: {
    name: 'Always',
    source: SelectivitySource.Always,
  },
  profiles: { ...listStateObject },
  selectedProfile: null,
  showInclusionToggle: false,
  showFirstVantageButton: false,
  fieldsByDocType: { ...listStateObject, nonStandaloneList: [] },
  canAddCriteria: false,
  hasVisitedSelectivity: false,
  hasInclusionOperationChanged: false,
  hasVantageQueryChanges: false,
  hasAnySelectivityDropdownChanged: false,
  previousSelectedSource: { name: 'Always', source: 0 },
  previousSelectedSourcesLength: 0,
  inclusionDropdownOpened: false,
  wheneverUnlessToggleChanged: false,
  selectivityTemplates: {
    templateList: [],
    gridDataState: { skip: 0, take: 20 },
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
  expandedTemplateDetails: null,
  maximumLogicalBlockId: 1,
};

export const fetchSelectivitySources = createAsyncThunk<
  IGetSelectivitySourcesResponse,
  IGetSelectivitySourcesRequest,
  { rejectValue: IErrorDetails }
>('selectivity/fetchSources', async (_, thunkApi) => {
  let response: IGetSelectivitySourcesResponse = { selectivitySourceDtos: [] };

  const client = Api.getSelectivityClient(thunkApi.dispatch);
  response = await client.getSelectivitySources(new GetSelectivitySourcesRequest());

  return response;
});

export const fetchCopyActions = createAsyncThunk<
  IGetAvailableSelectivityCopyActionsResponse,
  IGetAvailableSelectivityCopyActionsRequest,
  { rejectValue: IErrorDetails }
>('selectivity/fetchCopyActions', async (request, thunkApi) => {
  let response: IGetAvailableSelectivityCopyActionsResponse = { selectivityCopyActions: [] };

  const client = Api.getSelectivityClient(thunkApi.dispatch);
  response = await client.getAvailableSelectivityCopyActions(new GetAvailableSelectivityCopyActionsRequest(request));

  return response;
});

export const fetchSelectivityProfiles = createAsyncThunk<
  IGetAvailableSelectivityProfilesForMergeIntoMessageResponse,
  IGetAvailableSelectivityProfilesForMergeIntoMessageRequest,
  { rejectValue: IErrorDetails }
>('selectivity/fetchProfiles', async (request, thunkApi) => {
  let response: IGetAvailableSelectivityProfilesForMergeIntoMessageResponse = { availableSelectivityMergeInfos: [] };

  const client = Api.getSelectivityClient(thunkApi.dispatch);
  response = await client.getAvailableSelectivityProfilesForMergeIntoMessage(
    new GetAvailableSelectivityProfilesForMergeIntoMessageRequest(request),
  );

  return response;
});

export const OLM_FIELD_DEFAULT_NAME = 'Field';
export const OLM_FIELD_COMBINED_SELECTIVITY = 'combined selectivity';

export const fetchSelectivityFieldsByDocType = createAsyncThunk<
  IGetSelectivityFieldsByDocTypeResponse,
  IGetSelectivityFieldsByDocTypeRequest,
  { rejectValue: IErrorDetails }
>('selectivity/fetchFields', async (request, thunkApi) => {
  let response: IGetSelectivityFieldsByDocTypeResponse = {
    fields: [],
  };
  const client = Api.getSelectivityClient(thunkApi.dispatch);
  response = await client.getSelectivityFieldsByDocType(new GetSelectivityFieldsByDocTypeRequest(request));

  const defaultField = new OlmField();
  defaultField.init({
    name: OLM_FIELD_DEFAULT_NAME,
  });

  const defaultKeyValuePair = new KeyValuePairOfIntegerAndOlmField({
    key: 0,
    value: defaultField,
  });

  response.fields.unshift(defaultKeyValuePair);

  return response;
});

export const fetchSelctivityTemplates = createAsyncThunk<
  IGetSelectivityTemplatesResponse,
  IGetSelectivityTemplatesRequest,
  { rejectValue: IErrorDetails }
>('selectivity/selectivityTemplates', async (request, thunkApi) => {
  const client = Api.getSelectivityClient(thunkApi.dispatch);
  const response = await client.getSelectivityTemplates(new GetSelectivityTemplatesRequest(request));
  return response;
});

const selectivitySlice = createSlice({
  name: 'selectivity',
  initialState,
  reducers: {
    setSelectedCopyAction: (state, action: PayloadAction<ISelectivityAction | null>) => {
      state.selectedCopyAction = action.payload;
    },
    setSelectedSource: (state, action: PayloadAction<ISelectivitySource>) => {
      state.selectedSource = action.payload;
    },
    setSelectedProfile: (state, action: PayloadAction<ISelectivityProfile>) => {
      state.selectedProfile = action.payload;
    },
    setShowInclusionToggle: (state, action: PayloadAction<boolean>) => {
      state.showInclusionToggle = action.payload;
    },
    resetCopyActions: (state) => {
      state.copyActions.list = [];
    },
    clearProfiles: (state) => {
      state.profiles.list = [];
    },
    setCanAddCriteria: (state, action: PayloadAction<boolean>) => {
      state.canAddCriteria = action.payload;
    },
    setHasVisitedSelectivity: (state, action: PayloadAction<boolean>) => {
      state.hasVisitedSelectivity = action.payload;
    },
    setHasInclusionOperationChanged: (state, action: PayloadAction<boolean>) => {
      state.hasInclusionOperationChanged = action.payload;
    },
    setHasVantageQueryChanges: (state, action: PayloadAction<boolean>) => {
      state.hasVantageQueryChanges = action.payload;
    },
    setHasAnySelectivityDropdownChanged: (state, action: PayloadAction<boolean>) => {
      state.hasAnySelectivityDropdownChanged = action.payload;
    },
    setPreviousSelectedSource: (state, action: PayloadAction<ISelectivitySource>) => {
      state.previousSelectedSource = action.payload;
    },
    setPreviousSelectedSourcesLength: (state, action: PayloadAction<number>) => {
      state.previousSelectedSourcesLength = action.payload;
    },
    setInclusionDropdownOpened: (state, action: PayloadAction<boolean>) => {
      state.inclusionDropdownOpened = action.payload;
    },
    setWheneverUnlessToggleChanged: (state, action: PayloadAction<boolean>) => {
      state.wheneverUnlessToggleChanged = action.payload;
    },
    setTemplateMaintenanceGridDataState: (state, action: PayloadAction<State>) => {
      state.selectivityTemplates.gridDataState = {
        filter: action.payload.filter,
        group: action.payload.group,
        sort: action.payload.sort,
        skip: action.payload.skip,
        take: action.payload.take,
      };
    },
    setExpandTemplate: (state, action: PayloadAction<GridCellProps>) => {
      if (state.selectivityTemplates) {
        // create a copy of the templates (immutable) and update the expanded attribute of the clicked template
        state.selectivityTemplates.templateList = state.selectivityTemplates.templateList.map((template) => {
          if (template.primaryKey === action.payload.dataItem.primaryKey) {
            template.expanded = !action.payload.dataItem.expanded;
          }
          return template;
        });
      }
    },
    setExpandedTemplateDetails: (state, action: PayloadAction<IGetSelectivityTemplateDetailsResponse | null>) => {
      const { payload: template } = action;

      if (template && template.templateId > 0) {
        state.expandedTemplateDetails = {
          ...state.expandedTemplateDetails,
          [template.templateId]: { ...template, timeSet: Date.now() },
        };
      }
    },
    setMaximumLogicalBlockId: (state, action: PayloadAction<number>) => {
      state.maximumLogicalBlockId = action.payload;
    },
    clearExpandedTemplateDetails: (state) => {
      state.expandedTemplateDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSelectivitySources.pending, (state) => {
      state.sources.isRequestActive = true;
    });
    builder.addCase(fetchSelectivitySources.fulfilled, (state, action) => {
      state.sources.isRequestActive = false;
      state.sources.list = action.payload.selectivitySourceDtos.map((item) => {
        return { name: item.displayName, source: item.source };
      });
    });
    builder.addCase(fetchCopyActions.pending, (state) => {
      state.copyActions.isRequestActive = true;
    });
    builder.addCase(fetchCopyActions.fulfilled, (state, action) => {
      state.copyActions.isRequestActive = false;
      state.copyActions.list = action.payload.selectivityCopyActions.map((item) => {
        return { name: item.displayName, action: item.selectivityCopyAction };
      });
    });
    builder.addCase(fetchSelectivityProfiles.pending, (state) => {
      state.profiles.isRequestActive = true;
    });
    builder.addCase(fetchSelectivityProfiles.fulfilled, (state, action) => {
      state.profiles.isRequestActive = false;
      state.profiles.list = action.payload.availableSelectivityMergeInfos.map((item) => {
        const id = item.selectivityProfileUniqueId;
        return { name: item.displayName, profileUID: id, primaryKey: id };
      });
    });
    builder.addCase(fetchSelectivityFieldsByDocType.pending, (state) => {
      state.profiles.isRequestActive = true;
    });
    builder.addCase(fetchSelectivityFieldsByDocType.fulfilled, (state, action) => {
      state.profiles.isRequestActive = false;
      state.fieldsByDocType.list = action.payload.fields.map((item) => {
        return { key: item.key, value: item.value, name: item.value?.name || '' };
      });

      state.fieldsByDocType.nonStandaloneList = action.payload.fields
        .filter((item) => {
          if (item.value && !item.value.isStandaloneSelectivity)
            return { key: item.key, value: item.value, name: item.value?.name || '' };
        })
        .map((item) => {
          return { key: item.key, value: item.value, name: item.value?.name || '' };
        });
    });
    builder.addCase(fetchSelctivityTemplates.pending, (state) => {
      state.selectivityTemplates.isRequestActive = true;
    });
    builder.addCase(fetchSelctivityTemplates.fulfilled, (state, action) => {
      state.selectivityTemplates.isRequestActive = false;
      state.selectivityTemplates.templateList = action.payload.templates;
    });
    builder.addCase(fetchSelctivityTemplates.rejected, (state) => {
      state.selectivityTemplates.isRequestActive = false;
    });
  },
});

export const {
  setSelectedCopyAction,
  setSelectedSource,
  setSelectedProfile,
  setShowInclusionToggle,
  resetCopyActions,
  setCanAddCriteria,
  setHasVisitedSelectivity,
  setHasInclusionOperationChanged,
  setHasVantageQueryChanges,
  setHasAnySelectivityDropdownChanged,
  setPreviousSelectedSource,
  setPreviousSelectedSourcesLength,
  setInclusionDropdownOpened,
  setWheneverUnlessToggleChanged,
  setTemplateMaintenanceGridDataState,
  setExpandTemplate,
  setExpandedTemplateDetails,
  setMaximumLogicalBlockId,
  clearExpandedTemplateDetails,
  clearProfiles,
} = selectivitySlice.actions;

export default selectivitySlice.reducer;
